import React, { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { fetchUserOrganizations } from '@/modules/core/data/organizationRepository';
import { getUserRole } from '@/modules/core/data/roleRepository';
import { fetchLastOrgId, updateLastOrgId } from '@/modules/core/data/profileRepository';
import { checkUserSuspension, fetchUserActiveMemberships } from '@/modules/core/data/memberRepository';
import { supabase } from '@/integrations/supabase/client';
import type { Organization } from '@/types/database';

interface OrganizationContextType {
  organizations: Organization[];
  currentOrganization: Organization | null;
  currentRole: string | null;
  loading: boolean;
  isSuspended: boolean;
  setCurrentOrganization: (org: Organization | null) => void;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const CURRENT_ORG_KEY_PREFIX = 'lapyno_current_org';
const getCurrentOrgKey = (userId: string) => `${CURRENT_ORG_KEY_PREFIX}:${userId}`;
// Legacy key (pre-namespacing) — read once for backward compat
const LEGACY_CURRENT_ORG_KEY = 'lapyno_current_org';

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user, session } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrgState] = useState<Organization | null>(null);
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuspended, setIsSuspended] = useState(false);
  
  // Ref to avoid stale closure in realtime listener
  const currentOrgRef = useRef<Organization | null>(null);
  
  // Track the user ID we've already fetched for to prevent refetching on tab focus
  const fetchedForUserRef = useRef<string | null>(null);

  const fetchOrganizations = async (forceRefetch = false) => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrgState(null);
      setLoading(false);
      fetchedForUserRef.current = null;
      return;
    }

    // Skip if we've already fetched for this user (unless forced)
    if (!forceRefetch && fetchedForUserRef.current === user.id) {
      return;
    }

    try {
      const orgs = await fetchUserOrganizations(user.id);

      // If no active orgs, check if user has suspended memberships via security-definer RPC
      if (orgs.length === 0) {
        const hasSuspendedMembership = await checkUserSuspension();
        setIsSuspended(hasSuspendedMembership);
      } else {
        setIsSuspended(false);
      }

      setOrganizations(orgs);
      fetchedForUserRef.current = user.id;

      // Restore last selected org from localStorage (per-user namespaced key, with legacy fallback)
      const userKey = getCurrentOrgKey(user.id);
      let savedOrgId = localStorage.getItem(userKey);
      if (!savedOrgId) {
        const legacy = localStorage.getItem(LEGACY_CURRENT_ORG_KEY);
        if (legacy && orgs.find(o => o.id === legacy)) {
          savedOrgId = legacy;
          localStorage.setItem(userKey, legacy);
        }
      }

      // Cross-device persistence: fall back to server-side last_org_id on the profile
      if (!savedOrgId || !orgs.find(o => o.id === savedOrgId)) {
        const serverOrgId = await fetchLastOrgId(user.id);
        if (serverOrgId && orgs.find(o => o.id === serverOrgId)) {
          savedOrgId = serverOrgId;
          localStorage.setItem(userKey, serverOrgId);
        }
      }

      const savedOrg = orgs.find(o => o.id === savedOrgId);

      if (savedOrg) {
        setCurrentOrgState(savedOrg);
        fetchRole(savedOrg.id);
        updateLastOrgId(user.id, savedOrg.id);
      } else if (orgs.length > 0) {
        setCurrentOrgState(orgs[0]);
        localStorage.setItem(userKey, orgs[0].id);
        fetchRole(orgs[0].id);
        updateLastOrgId(user.id, orgs[0].id);
      }
    } catch (err) {
      console.error('Error in fetchOrganizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRole = async (orgId: string) => {
    if (!user) return;
    try {
      const role = await getUserRole(orgId);
      setCurrentRole(role);
    } catch (err) {
      console.error('Error in fetchUserRole:', err);
    }
  };

  useEffect(() => {
    if (session && user) {
      // Check if we need to force a refresh (e.g., after accepting an invitation)
      const forceRefresh = localStorage.getItem('lapyno_force_org_refresh') === 'true';
      if (forceRefresh) {
        localStorage.removeItem('lapyno_force_org_refresh');
        fetchOrganizations(true); // Force refetch
      } else {
        fetchOrganizations();
      }
    } else if (!session) {
      setOrganizations([]);
      setCurrentOrgState(null);
      setCurrentRole(null);
      setIsSuspended(false);
      setLoading(false);
      fetchedForUserRef.current = null;
    }
  }, [user?.id]); // Only depend on user.id, not the entire object

  // Keep ref in sync
  useEffect(() => {
    currentOrgRef.current = currentOrganization;
  }, [currentOrganization]);

  // Listen for realtime changes to user's membership (suspension, removal, org deletion)
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('org-membership-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'core_organization_members',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          const newStatus = (payload.new as any)?.status;
          const affectedOrgId = (payload.new as any)?.org_id || (payload.old as any)?.org_id;
          const currentOrgId = currentOrgRef.current?.id;

          // If status changed to suspended or removed for current org
          if (
            (newStatus === 'suspended' || newStatus === 'removed') &&
            affectedOrgId === currentOrgId
          ) {
            console.log(`[OrgContext] Membership changed to ${newStatus} for current org, checking other orgs...`);
            
            if (newStatus === 'suspended') {
              // Immediately block access — don't wait for any async check
              setIsSuspended(true);
              setOrganizations([]);
              setCurrentOrgState(null);
              setCurrentRole(null);
              localStorage.removeItem(getCurrentOrgKey(user.id));
              
              // Then check if user has other active memberships to potentially switch
              const activeMemberships = await fetchUserActiveMemberships(user.id);
              if (activeMemberships.length > 0) {
                // Has other active orgs — switch to next one
                setIsSuspended(false);
                fetchedForUserRef.current = null;
                fetchOrganizations(true);
              }
            } else {
              // Removed — check for other orgs first
              localStorage.removeItem(getCurrentOrgKey(user.id));
              const activeMemberships = await fetchUserActiveMemberships(user.id);
              if (activeMemberships.length === 0) {
                await supabase.auth.signOut();
                const msg = 'Votre accès a été révoqué. Veuillez contacter un administrateur.';
                window.location.href = `/auth?error=${encodeURIComponent(msg)}`;
              } else {
                fetchedForUserRef.current = null;
                fetchOrganizations(true);
              }
            }
            return;
          } else if (newStatus === 'active' && affectedOrgId) {
            // Reactivated - refresh to pick up the org again
            setIsSuspended(false);
            fetchedForUserRef.current = null;
            fetchOrganizations(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Listen for realtime role changes (e.g., admin demoted to member by an owner)
  // We must refresh currentRole immediately to enforce access control without page reload.
  useEffect(() => {
    if (!user || !currentOrganization?.id) return;
    const orgId = currentOrganization.id;

    const channel = supabase
      .channel(`org-role-changes:${orgId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'core_member_roles' },
        () => {
          // Re-fetch role for current org. getUserRole resolves via auth.uid() → membership → role.
          fetchRole(orgId);
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'core_roles', filter: `org_id=eq.${orgId}` },
        () => {
          fetchRole(orgId);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, currentOrganization?.id]);

  const setCurrentOrganization = (org: Organization | null) => {
    setCurrentOrgState(org);
    if (!user) return;
    const userKey = getCurrentOrgKey(user.id);
    if (org) {
      localStorage.setItem(userKey, org.id);
      fetchRole(org.id);
      updateLastOrgId(user.id, org.id);
    } else {
      localStorage.removeItem(userKey);
      setCurrentRole(null);
    }
  };

  const refreshOrganizations = async () => {
    await fetchOrganizations(true); // Force refetch
  };

  // Batch 5A (Q3): memoize the context value to avoid cascading re-renders of
  // every `useOrganization()` consumer when an unrelated state changes in the
  // provider. `setCurrentOrganization` and `refreshOrganizations` are stable
  // closures over state setters; keeping them in the deps array is safe and
  // matches the actual semantic identity of the exposed value.
  const value = useMemo<OrganizationContextType>(
    () => ({
      organizations,
      currentOrganization,
      currentRole,
      loading,
      isSuspended,
      setCurrentOrganization,
      refreshOrganizations,
    }),
    [organizations, currentOrganization, currentRole, loading, isSuspended],
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
